/**
 * useGameBoardScoring Composable
 *
 * Orchestrates canasta highlighting on the GameBoard during end-of-round scoring.
 * Highlights actual meld elements on the board (instead of modal popup) with
 * voice-synchronized counting animations.
 *
 * Features:
 * - DOM-based meld element querying by team container
 * - Categorization by canasta type (clean, dirty, wild)
 * - Sequential highlighting with CSS class transitions
 * - Voice synchronization using Web Speech API
 * - Base total announcement after counting
 *
 * @example
 * const { highlightTeamCanastas } = useGameBoardScoring()
 * await highlightTeamCanastas({
 *   team: 'A',
 *   canastas: { natural: 2, mixed: 1, wild: 0 },
 *   teamName: 'The Aces',
 *   isPlayerTeam: true
 * })
 */

import { ref } from 'vue'
import { useSounds } from './useSounds'
import { useAnimations } from './useAnimations'

/**
 * Canasta data for scoring
 */
export interface CanastaData {
  natural: number  // Clean canastas (500 pts each)
  mixed: number    // Dirty canastas (300 pts each)
  wild: number     // Wild canastas (1000 pts each)
}

/**
 * Request parameters for canasta highlighting
 */
export interface CanastaHighlightRequest {
  team: 'A' | 'B'
  canastas: CanastaData
  teamName: string
  isPlayerTeam: boolean  // true = my-canastas, false = opponent-canastas
}

/**
 * Categorized meld elements from DOM
 */
interface CategorizedMelds {
  clean: HTMLElement[]
  dirty: HTMLElement[]
  wild: HTMLElement[]
}

/**
 * Point values for canasta types (for counting increments)
 * Divided by 100 for counting sequence (e.g., 500 -> count by 5)
 */
const CANASTA_COUNT_INCREMENT = {
  clean: 5,   // 500 / 100
  dirty: 3,   // 300 / 100
  wild: 10,   // 1000 / 100
} as const

/**
 * Actual point values for totals
 */
const CANASTA_POINTS = {
  clean: 500,
  dirty: 300,
  wild: 1000,
} as const

/**
 * useGameBoardScoring Composable
 */
export function useGameBoardScoring() {
  const { speak, speakCounting, playPileHighlight, playCanastaComplete } = useSounds()
  const { reducedMotion, animationSpeed } = useAnimations()

  // Scoring state
  const isScoring = ref(false)
  const currentTeamScoring = ref<'A' | 'B' | null>(null)

  /**
   * Get container ID for team melds
   */
  function getContainerId(isPlayerTeam: boolean): string {
    return isPlayerTeam ? 'my-canastas' : 'opponent-canastas'
  }

  /**
   * Query DOM for meld elements and categorize by type
   * Only includes completed canastas (7+ cards, has .rolled-up class)
   *
   * @param containerId - DOM ID of the canastas container
   */
  function getMeldElementsByType(containerId: string): CategorizedMelds {
    const container = document.getElementById(containerId)
    if (!container) {
      console.warn(`[useGameBoardScoring] Container #${containerId} not found`)
      return { clean: [], dirty: [], wild: [] }
    }

    // Select all meld visuals that are canastas (have rolled-up class)
    const allMelds = container.querySelectorAll<HTMLElement>(
      '.canasta-meld-visual.rolled-up'
    )

    const clean: HTMLElement[] = []
    const dirty: HTMLElement[] = []
    const wild: HTMLElement[] = []

    allMelds.forEach((meld) => {
      // Check meld type by CSS class
      if (meld.classList.contains('wild-meld')) {
        wild.push(meld)
      } else if (meld.classList.contains('dirty-meld')) {
        dirty.push(meld)
      } else if (meld.classList.contains('natural-meld')) {
        clean.push(meld)
      } else {
        // Fallback: check data attribute for wild status
        const isWildMeld = meld.dataset.isWildMeld === 'true'
        if (isWildMeld) {
          wild.push(meld)
        } else {
          // Default to clean if no explicit type (defensive)
          clean.push(meld)
        }
      }
    })

    return { clean, dirty, wild }
  }

  /**
   * Build counting numbers array for a canasta type
   * Returns cumulative values: [5, 10, 15] for 3 clean canastas
   */
  function buildCountNumbers(count: number, increment: number): number[] {
    const numbers: number[] = []
    let running = 0
    for (let i = 0; i < count; i++) {
      running += increment
      numbers.push(running)
    }
    return numbers
  }

  /**
   * Estimate voice duration for counting sequence
   * Based on text length + base time
   */
  function estimateVoiceDuration(numbers: number[], suffix: string = ''): number {
    if (numbers.length === 0) return 0
    const countingText = numbers.join(', ')
    const fullText = suffix ? `${countingText}, ${suffix}` : countingText
    // Base time + per-character estimate (150ms per char)
    return 1000 + fullText.length * 150
  }

  /**
   * Delay helper that respects animation speed and reduced motion
   */
  function delay(ms: number): Promise<void> {
    const adjustedMs = reducedMotion.value ? 100 : ms / animationSpeed.value
    return new Promise((resolve) => setTimeout(resolve, adjustedMs))
  }

  /**
   * Add scoring-active class to container for dimming non-highlighted melds
   */
  function setContainerScoringState(containerId: string, active: boolean): void {
    const container = document.getElementById(containerId)
    if (container) {
      if (active) {
        container.classList.add('scoring-active')
      } else {
        container.classList.remove('scoring-active')
      }
    }
  }

  /**
   * Clear all scoring classes from meld elements
   */
  function clearScoringClasses(containerId: string): void {
    const container = document.getElementById(containerId)
    if (!container) return

    const melds = container.querySelectorAll<HTMLElement>('.canasta-meld-visual')
    melds.forEach((meld) => {
      meld.classList.remove('scoring-highlight', 'scored')
    })
    container.classList.remove('scoring-active')
  }

  /**
   * Highlight meld elements sequentially in sync with voice timing
   *
   * @param elements - Array of meld DOM elements to highlight
   * @param voiceDuration - Estimated voice duration in ms
   */
  async function highlightMeldsSequentially(
    elements: HTMLElement[],
    voiceDuration: number
  ): Promise<void> {
    if (elements.length === 0) return

    // Calculate interval between highlights
    // Use 70% of voice duration so visuals finish slightly before voice
    const interval = (voiceDuration * 0.7) / elements.length

    for (let i = 0; i < elements.length; i++) {
      const meld = elements[i]
      if (!meld) continue

      // Play tick sound
      playPileHighlight()

      // Add highlight class (triggers CSS animation)
      meld.classList.add('scoring-highlight')

      // Wait for this meld's time window
      await delay(interval)

      // Transition to scored state
      meld.classList.remove('scoring-highlight')
      meld.classList.add('scored')
    }
  }

  /**
   * Count and highlight canastas of a specific type
   * Runs voice counting and visual highlighting in parallel
   *
   * @param elements - Meld elements for this type
   * @param count - Number of canastas (from scoring data)
   * @param increment - Count increment (5, 3, or 10)
   * @param typeName - Type name for logging
   */
  async function countCanastaType(
    elements: HTMLElement[],
    count: number,
    increment: number,
    _typeName: string
  ): Promise<number> {
    if (count === 0) return 0

    // Build counting numbers
    const numbers = buildCountNumbers(count, increment)
    const voiceDuration = estimateVoiceDuration(numbers)

    // Run voice and highlighting in parallel
    const highlightPromise = highlightMeldsSequentially(
      elements.slice(0, count), // Only highlight up to count elements
      voiceDuration
    )

    const voicePromise = speakCounting(numbers, '')

    await Promise.all([highlightPromise, voicePromise])

    // Return total points for this type
    return numbers[numbers.length - 1]! * 100
  }

  /**
   * Show base points popup in center of screen
   * Returns when popup has been displayed and faded out
   */
  async function showBasePointsPopup(points: number): Promise<void> {
    // Remove any existing popup
    const existing = document.querySelector('.base-points-popup')
    if (existing) {
      existing.remove()
    }

    // Create popup element using safe DOM methods
    const popup = document.createElement('div')
    popup.className = 'base-points-popup'

    const labelDiv = document.createElement('div')
    labelDiv.className = 'base-label'
    labelDiv.textContent = 'Base'

    const valueDiv = document.createElement('div')
    valueDiv.className = 'base-value'
    valueDiv.textContent = `${points.toLocaleString()} points`

    popup.appendChild(labelDiv)
    popup.appendChild(valueDiv)

    // Add to body
    document.body.appendChild(popup)

    // Play a sound for emphasis
    playCanastaComplete()

    // Announce the base points
    speak(`Your base is ${points.toLocaleString()}`, 'announcer')

    // Show for 2 seconds
    await delay(2000)

    // Extra pause to ensure voice completes before modal picks up
    await delay(1000)

    // Fade out
    popup.classList.add('fade-out')
    await delay(300)

    // Remove from DOM
    popup.remove()
  }

  /**
   * Main orchestrator: Highlight all canastas for a team
   * Counts in order: clean -> dirty -> wild
   * Then shows "Base X points" popup
   *
   * @param request - Highlight request with team and canasta data
   * @param onComplete - Optional callback when highlighting completes
   */
  async function highlightTeamCanastas(
    request: CanastaHighlightRequest,
    onComplete?: () => void
  ): Promise<void> {
    const { team, canastas, isPlayerTeam } = request
    const containerId = getContainerId(isPlayerTeam)

    // Set scoring state
    isScoring.value = true
    currentTeamScoring.value = team

    // Clear any previous scoring classes
    clearScoringClasses(containerId)

    // Get meld elements from DOM
    const elements = getMeldElementsByType(containerId)

    // Activate container scoring mode (dims non-highlighted melds)
    setContainerScoringState(containerId, true)

    let totalBase = 0
    const hasAnyCanastas = canastas.natural > 0 || canastas.mixed > 0 || canastas.wild > 0

    try {
      if (hasAnyCanastas) {
        // Brief intro pause
        await delay(500)

        // Count clean canastas (5, 10, 15...)
        if (canastas.natural > 0) {
          playCanastaComplete()
          const cleanTotal = await countCanastaType(
            elements.clean,
            canastas.natural,
            CANASTA_COUNT_INCREMENT.clean,
            'clean'
          )
          totalBase += cleanTotal
          await delay(300) // Brief pause between types
        }

        // Count dirty canastas (3, 6, 9...)
        if (canastas.mixed > 0) {
          if (canastas.natural > 0) {
            playCanastaComplete()
          }
          const dirtyTotal = await countCanastaType(
            elements.dirty,
            canastas.mixed,
            CANASTA_COUNT_INCREMENT.dirty,
            'dirty'
          )
          totalBase += dirtyTotal
          await delay(300)
        }

        // Count wild canastas (10, 20...)
        if (canastas.wild > 0) {
          if (canastas.natural > 0 || canastas.mixed > 0) {
            playCanastaComplete()
          }
          const wildTotal = await countCanastaType(
            elements.wild,
            canastas.wild,
            CANASTA_COUNT_INCREMENT.wild,
            'wild'
          )
          totalBase += wildTotal
          await delay(300)
        }
      }

      // Show base points popup
      await delay(300)
      await showBasePointsPopup(totalBase)

    } finally {
      // Clean up scoring state
      setContainerScoringState(containerId, false)
      isScoring.value = false
      currentTeamScoring.value = null

      // Trigger completion callback
      onComplete?.()
    }
  }

  /**
   * Calculate total canasta base points
   * Utility for external use
   */
  function calculateCanastaBase(canastas: CanastaData): number {
    return (
      canastas.natural * CANASTA_POINTS.clean +
      canastas.mixed * CANASTA_POINTS.dirty +
      canastas.wild * CANASTA_POINTS.wild
    )
  }

  return {
    // Main orchestrator
    highlightTeamCanastas,

    // Utilities
    calculateCanastaBase,
    getMeldElementsByType,
    clearScoringClasses,

    // State
    isScoring,
    currentTeamScoring,
  }
}
